import React from "react";
import { productUpdatesRequest } from "../lib/productUpdatesApi.js";
import { validTextMaxWords } from "./ProductUpdateTextLength.jsx";

const wordCount = text => text.trim() ? text.trim().split(/\s+/u).length : 0;
const localName = options => options?.engine === "claude" ? "Claude" : "Codex";

// Notice text with its tools: Gras, then correction or rephrasing by the local tool, reviewed before saving.
export default function ProductUpdateTextField({ value, onChange, run, busy = false, maxWords = null, codexLocalAvailable = false, codexOptions }) {
  const field = React.useRef(null);
  // The new value resets the cursor: the selected words stay selected once rendered.
  const selection = React.useRef(null);
  React.useLayoutEffect(() => {
    const pending = selection.current;
    selection.current = null;
    if (pending && field.current) { field.current.focus(); field.current.setSelectionRange(...pending); }
  }, [value]);
  // **…** around the selection shows those words in bold in the Journal; again removes them.
  const bold = () => {
    if (!field.current) return;
    const { selectionStart: start, selectionEnd: end } = field.current;
    const wrapped = start >= 2 && value.slice(start - 2, start) === "**" && value.slice(end, end + 2) === "**";
    onChange(wrapped ? value.slice(0, start - 2) + value.slice(start, end) + value.slice(end + 2) : `${value.slice(0, start)}**${value.slice(start, end)}**${value.slice(end)}`);
    const offset = wrapped ? -2 : 2;
    selection.current = [start + offset, end + offset];
  };
  const [revising, setRevising] = React.useState(null);
  const revise = action => void run(async () => {
    setRevising(action);
    try {
      const { text } = await productUpdatesRequest("/text/revise", { method: "POST", body: { text: value, action, generator: codexOptions?.engine === "claude" ? "claude_local" : "codex_local",
        codex_model: codexOptions.model, codex_fast_mode: codexOptions.fast, codex_effort: codexOptions.effort } });
      onChange(text);
    } finally { setRevising(null); }
  }, false, action === "rephrase" ? "Le texte a été reformulé. Relisez-le, puis enregistrez." : "Le texte a été corrigé. Relisez-le, puis enregistrez.");
  const limited = maxWords !== null && validTextMaxWords(maxWords);
  return <>
    <label>Texte<textarea rows={4} value={value} ref={field} onChange={event => onChange(event.target.value)}
      onKeyDown={event => { if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "b") { event.preventDefault(); bold(); } }} /></label>
    <small className={limited && wordCount(value) > Number(maxWords) ? "pu-error" : ""}>{wordCount(value)}{limited ? ` / ${maxWords}` : ""} mots</small>
    <div className="pu-actions">
      <button type="button" className="pu-button" title="Entoure la sélection de ** : ces mots s’affichent en gras dans le Journal (Ctrl+B)." onMouseDown={event => event.preventDefault()} onClick={bold}><strong>G</strong> Gras</button>
      {codexLocalAvailable && [["correct", "Corriger le texte", "Correction…"], ["rephrase", "Reformuler", "Reformulation…"]].map(([action, label, pending]) =>
        <button key={action} type="button" className="pu-button" disabled={busy || !value.trim() || !codexOptions?.ready} onClick={() => revise(action)}>
          {revising === action ? pending : `${label} avec ${localName(codexOptions)}`}</button>)}
    </div>
  </>;
}
